import type { LocalDataColumn } from "@nymbl/nos2-rules";
import { CALENDAR_RULES_HELP } from "@/lib/column-help";
import { CALENDAR_RULE_KIND_LABELS, CALENDAR_RULE_SOURCE_LABELS } from "@/lib/enums";
import { todayInZone } from "@/lib/time/dates";
import type { CalendarRuleView } from "@/schemas/calendar";

const asOptions = (labels: Readonly<Record<string, string>>) =>
  Object.entries(labels).map(([value, label]) => ({ value, label }));

/** The day a rule was last used, in the app's clock (America/New_York); null when never. */
function lastUsedDate(rule: CalendarRuleView): string | null {
  return rule.lastUsedAt ? todayInZone(new Date(rule.lastUsedAt)) : null;
}

/** Profile's rules table: Type, Matches, Task, Source, Used, Last used (each with its help). */
export function ruleColumns(): LocalDataColumn<CalendarRuleView>[] {
  return [
    {
      key: "type",
      label: "Type",
      type: "enum",
      accessor: (r) => r.kind,
      options: asOptions(CALENDAR_RULE_KIND_LABELS),
      help: CALENDAR_RULES_HELP.type,
    },
    {
      key: "matches",
      label: "Matches",
      type: "text",
      accessor: (r) => r.label,
      help: CALENDAR_RULES_HELP.matches,
    },
    {
      key: "task",
      label: "Task",
      type: "text",
      accessor: (r) => r.task?.taskName ?? null,
      searchText: (r) => r.task?.projectName ?? "",
      help: CALENDAR_RULES_HELP.task,
    },
    {
      key: "source",
      label: "Source",
      type: "enum",
      accessor: (r) => r.source,
      options: asOptions(CALENDAR_RULE_SOURCE_LABELS),
      help: CALENDAR_RULES_HELP.source,
    },
    {
      key: "used",
      label: "Used",
      type: "number",
      accessor: (r) => r.hits,
      help: CALENDAR_RULES_HELP.used,
    },
    {
      key: "lastUsed",
      label: "Last used",
      type: "date",
      accessor: lastUsedDate,
      help: CALENDAR_RULES_HELP.lastUsed,
    },
  ];
}

/** The row's "⋯" button name, unique per rule (kind and label): `Actions for rule Title is “Standup”`. */
export function ruleActionsLabel(rule: CalendarRuleView): string {
  return `Actions for rule ${CALENDAR_RULE_KIND_LABELS[rule.kind]} “${rule.label}”`;
}
